import { ReactNode, useEffect } from "react";
import { Box, ButtonBase, IconButton, MenuItem, Select, Skeleton, Tooltip, Typography, useTheme } from "@mui/material";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";

// ----------------------------------------------------------------------

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Fração do grid (ex.: "2.2fr"); default "1fr" */
  width?: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyState?: ReactNode;
  /** 1-based */
  page: number;
  pageSize: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Largura mínima (scroll-x em telas estreitas) */
  minWidth?: number;
  /** Rótulo de contagem exibido à direita do rodapé (ex.: "3 de 8 itens"). Padrão: "{N} itens". */
  countLabel?: string;
}

/** Tabela declarativa do redesign (histórico, pacientes, CRUDs do admin) */
export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  emptyState,
  page,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  minWidth,
  countLabel,
}: DataTableProps<T>) {
  const theme = useTheme();
  const gridTemplateColumns = columns.map((column) => column.width ?? "1fr").join(" ");
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages && totalRows > 0) {
      onPageChange(totalPages);
    }
  }, [page, totalPages, totalRows, onPageChange]);

  const cellSx = (column: DataTableColumn<T>) => ({
    textAlign: column.align ?? "left",
    minWidth: 0,
  });

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "18px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              gap: 1.5,
              p: "16px 24px",
              backgroundColor: theme.palette.brand.surface2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  ...cellSx(column),
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                {column.header}
              </Typography>
            ))}
          </Box>

          {loading &&
            [0, 1, 2, 3].map((index) => (
              <Box key={index} sx={{ p: "13px 24px" }}>
                <Skeleton height={34} />
              </Box>
            ))}

          {!loading && rows.length === 0 && <Box sx={{ p: 2 }}>{emptyState}</Box>}

          {!loading &&
            rows.map((row) => (
              <ButtonBase
                key={rowKey(row)}
                component="div"
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  display: "grid",
                  gridTemplateColumns,
                  gap: 1.5,
                  width: "100%",
                  p: "13px 24px",
                  textAlign: "left",
                  alignItems: "center",
                  cursor: onRowClick ? "pointer" : "default",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:hover": onRowClick ? { backgroundColor: theme.palette.brand.surface2 } : undefined,
                }}
              >
                {columns.map((column) => (
                  <Box key={column.key} sx={cellSx(column)}>
                    {column.render(row)}
                  </Box>
                ))}
              </ButtonBase>
            ))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: "16px 24px", flexWrap: "wrap" }}>
        {onPageSizeChange ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>Linhas por página</Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              inputProps={{ "aria-label": "Linhas por página" }}
              sx={{ fontSize: "13px" }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Box />
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px", color: "text.secondary", mr: 0.5 }}>
            {countLabel ?? `${totalRows} ${totalRows === 1 ? "item" : "itens"}`}
          </Typography>
          <Tooltip title="Página anterior" placement="top">
            <span>
              <IconButton aria-label="Página anterior" disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)} sx={{ width: 34, height: 34, borderRadius: "9px", border: `1.5px solid ${theme.palette.divider}` }}>
                <ChevronLeftRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Próxima página" placement="top">
            <span>
              <IconButton aria-label="Próxima página" disabled={safePage >= totalPages} onClick={() => onPageChange(safePage + 1)} sx={{ width: 34, height: 34, borderRadius: "9px", border: `1.5px solid ${theme.palette.divider}` }}>
                <ChevronRightRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
